import Swal from "sweetalert2";

export async function postData(url, data, method = "POST") {
  try {
    const options = {
      method,
      headers: {
        "Content-Type": "application/json",
      },
    };

    if (data && method !== "GET") {
      options.body = JSON.stringify(data);
    }

    const response = await fetch(url, options);
    const body = await response.json();

    return {
      ok: response.ok,
      status: response.status,
      body: body,
    };
  } catch (error) {
    console.error("postData error:", error);
    throw error;
  }
}

export async function postFileData(url, formData) {
  try {
    const response = await fetch(url, {
      method: "POST",
      body: formData,
    });

    const body = await response.json();

    return {
      ok: response.ok,
      status: response.status,
      body: body,
    };
  } catch (error) {
    console.error("postFileData error:", error);
    throw error;
  }
}

export function debounce(func, delay, immediate = false) {
  let timeout;

  const debounced = function (...args) {
    const later = () => {
      timeout = null;
      if (!immediate) {
        func.apply(this, args);
      }
    };

    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, delay);

    if (callNow) {
      func.apply(this, args);
    }
  };

  debounced.cancel = function () {
    clearTimeout(timeout);
    timeout = null;
  };

  return debounced;
}

export const getFormDataAsObject = (form) => {
  return Object.fromEntries(new FormData(form).entries());
};

export const showAlert = async ({
  icon,
  text,
  title,
  isToast = false,
  toastOptions = {},
  ...options
}) => {
  const baseConfig = {
    text,
    customClass: {},
    heightAuto: false,
    ...options,
  };

  switch (icon) {
    case "success":
      baseConfig.title = title || "Επιτυχία!";
      baseConfig.customClass.confirmButton = "btn btn-success";
      break;
    case "error":
      baseConfig.title = title || "Σφάλμα!";
      baseConfig.customClass.confirmButton = "btn btn-danger";
      break;
    case "warning":
      baseConfig.title = title || "Προσοχή!";
      baseConfig.customClass.confirmButton = "btn btn-warning";
      break;
    default:
      baseConfig.title = title || "Ειδοποίηση";
      break;
  }

  if (isToast) {
    return Swal.fire({
      ...baseConfig,
      icon,
      toast: true,
      position: toastOptions.position ?? "top-end",
      timer: toastOptions.timer ?? 3000,
      timerProgressBar: true,
      showConfirmButton: false,
      ...toastOptions,
    });
  }

  return Swal.fire({
    ...baseConfig,
    icon,
  });
};

export const showConfirm = async ({
  text,
  title = "Είστε σίγουροι;",
  confirmButtonText = "Ναι",
  ...options
}) => {
  return Swal.fire({
    title,
    text,
    icon: "warning",
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText: "Άκυρο",
    customClass: {
      confirmButton: "btn btn-primary",
      cancelButton: "btn btn-danger",
    },
    ...options,
  });
};

export const toggleButtonLoading = (button, isLoading) => {
  if (!button) return;

  const label = button.querySelector(".indicator-label");
  const progress = button.querySelector(".indicator-progress");

  button.disabled = isLoading;

  label?.classList.toggle("hidden", isLoading);
  progress?.classList.toggle("hidden", !isLoading);
};

export const toggleFormLock = (form, isLocked) => {
  if (!form) return;

  const elements = form.querySelectorAll("input, button, textarea, select");
  elements.forEach((el) => {
    el.disabled = isLocked;
  });

  const anchorElements = form.querySelectorAll("a");
  anchorElements.forEach((el) => {
    el.classList.toggle("pointer-events-none", isLocked);
    el.classList.toggle("opacity-50", isLocked);

    if (isLocked) {
      el.setAttribute("tabindex", "-1");
    } else {
      el.removeAttribute("tabindex");
    }
  });
};

export const setFormProcessing = (form, isProcessing, submitButton = null) => {
  if (!form) return;
  const button = submitButton || form.querySelector('button[type="submit"]');
  toggleButtonLoading(button, isProcessing);
  toggleFormLock(form, isProcessing);
};

export const trimValues = (obj) => {
  if (obj && typeof obj === "object") {
    for (const key in obj) {
      if (
        Object.prototype.hasOwnProperty.call(obj, key) &&
        typeof obj[key] === "string"
      ) {
        obj[key] = obj[key].trim();
      }
    }
  }

  return obj;
};

export const getTrimmedFormData = (form) => {
  if (!(form instanceof HTMLFormElement)) {
    console.error("getTrimmedFormData expects an HTMLFormElement:", form);
    return new FormData();
  }

  const originalFormData = new FormData(form);
  const trimmedFormData = new FormData();

  for (const [key, value] of originalFormData.entries()) {
    if (typeof value === "string") {
      trimmedFormData.append(key, value.trim());
    } else {
      trimmedFormData.append(key, value);
    }
  }

  return trimmedFormData;
};

export function preventEnterSubmit(formElement) {
  if (!formElement) return;

  formElement.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && e.target.matches("input")) {
      e.preventDefault();

      const focusableElements = Array.from(
        formElement.querySelectorAll("input, textarea, select")
      );
      const currentIndex = focusableElements.indexOf(e.target);
      const nextElement = focusableElements[currentIndex + 1];

      nextElement?.focus();
      setTimeout(() => {
        nextElement?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  });
}

export const showTableLoader = (
  target,
  message = "Φόρτωση. . .",
  colspan = 1
) => {
  if (!target) return;

  const tbody =
    target.tagName.toLowerCase() === "table"
      ? target.querySelector("tbody") ||
        target.appendChild(document.createElement("tbody"))
      : target;

  tbody.innerHTML = `
    <tr>
      <td colspan="${colspan}" class="text-center py-4 text-gray-500">
        <div class="flex items-center justify-center gap-2">
          <span>${message}</span>
          <svg class="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
          </svg>
        </div>
      </td>
    </tr>
  `;
};

export const getTimeAgo = (dateString, compact = false) => {
  const now = new Date();
  const date = new Date(dateString);

  if (isNaN(date.getTime())) return "Invalid Date";

  const seconds = Math.floor((now - date) / 1000);
  const units = [
    {
      limit: 60,
      name: "just_now",
    },
    {
      limit: 3600,
      div: 60,
      full: ["λεπτό", "λεπτά"],
      short: "λ.",
    },
    {
      limit: 86400,
      div: 3600,
      full: ["ώρα", "ώρες"],
      short: "ω.",
    },
    {
      limit: 2592000,
      div: 86400,
      full: ["ημέρα", "ημέρες"],
      short: "ημ.",
    },
    {
      limit: 31536000,
      div: 2592000,
      full: ["μήνα", "μήνες"],
      short: "μήν.",
    },
    {
      limit: Infinity,
      div: 31536000,
      full: ["χρόνο", "χρόνια"],
      short: "χρ.",
    },
  ];

  const unit = units.find((u) => seconds < u.limit);
  if (!unit || unit.name === "just_now") return "Μόλις";

  const value = Math.floor(seconds / unit.div);

  let unitText;
  if (compact) {
    unitText = unit.short;
  } else {
    unitText = value === 1 ? unit.full[0] : unit.full[1];
  }

  return `${value} ${unitText} πριν`;
};

export const formatDateDMHI = (input) => {
  const d = new Date((input || "").replace(" ", "T"));
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");

  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

export const escapeHTML = (str) => {
  if (!str) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};
